import { initializeApp } from 'firebase/app';
import { 
  getFirestore
} from 'firebase/firestore';
import { 
  getAuth,
  GoogleAuthProvider, 
  FacebookAuthProvider,
  signInWithPopup, 
  RecaptchaVerifier, 
  setPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  browserSessionPersistence,
  Auth,
  onAuthStateChanged
} from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Auth
const auth = getAuth(app);

// Set persistence to LOCAL (strongest) to ensure session is maintained across page refreshes
// Try multiple persistence methods in case one fails
const setupPersistence = async () => {
  try {
    // First try indexedDBLocalPersistence (strongest)
    await setPersistence(auth, indexedDBLocalPersistence);
    console.log('Firebase Auth persistence set to indexedDBLocalPersistence');
  } catch (error) {
    console.warn('Failed to set indexedDBLocalPersistence, falling back to browserLocalPersistence', error);
    
    try {
      // Fall back to browserLocalPersistence if indexedDB fails
      await setPersistence(auth, browserLocalPersistence);
      console.log('Firebase Auth persistence set to browserLocalPersistence');
    } catch (error) {
      console.warn('Failed to set browserLocalPersistence, falling back to browserSessionPersistence', error);
      
      try {
        // Last resort: use session persistence
        await setPersistence(auth, browserSessionPersistence);
        console.log('Firebase Auth persistence set to browserSessionPersistence');
      } catch (error) {
        console.error('All persistence methods failed:', error);
      }
    }
  }
};

// Run the persistence setup
setupPersistence();

// Add an auth state listener to ensure token is refreshed
onAuthStateChanged(auth, async (user) => {
  if (user) {
    try {
      // Get a fresh token and store it in localStorage
      const token = await user.getIdToken(true);
      localStorage.setItem('token', token);
      localStorage.setItem('strategiz_user', JSON.stringify({
        id: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL
      }));
      console.log('Auth state changed: User is signed in, token refreshed');
    } catch (error) {
      console.error('Error refreshing token in auth state change:', error);
    }
  } else {
    console.log('Auth state changed: User is signed out');
  }
});

// Initialize Firestore
const db = getFirestore(app);

// Initialize analytics only in production
const analytics = process.env.NODE_ENV === 'production' ? getAnalytics(app) : null;

// Export the initialized Firebase app and services
export { app, auth, db, analytics };
